/**
 * Notifications Modal Component
 * Displays a list of notifications from API
 * Design based on mock-html/viewer/notifications.html
 */

import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { apiClient, Notification as ApiNotification } from '../lib/api-client';
import { translationClient, NotificationTranslation } from '../lib/translation';
import { AnalyticsLogger } from '../lib/analytics';
import { resolveMediaUrl } from '../lib/media-url';
import NotificationImage from './NotificationImage';

interface NotificationsModalProps {
    isOpen: boolean;
    onClose: () => void;
}

// These functions now use the t function passed as parameter
const getSenderTypeLabel = (senderType: string, t: (key: string) => string): string => {
    switch (senderType) {
        case 'admin':
            return t('notifications.senderTypes.admin');
        case 'manager':
            return t('notifications.senderTypes.manager');
        case 'streamer':
            return t('notifications.senderTypes.streamer');
        default:
            return senderType;
    }
};

const formatTime = (dateString: string, t: (key: string, options?: Record<string, unknown>) => string, i18n: { language: string }): string => {
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
    const diffDays = Math.floor(diffHours / 24);

    if (diffHours < 1) {
        return t('notifications.time.justNow');
    } else if (diffHours < 24) {
        return t('notifications.time.hoursAgo', { count: diffHours });
    } else if (diffDays < 7) {
        return t('notifications.time.daysAgo', { count: diffDays });
    } else {
        return date.toLocaleDateString(i18n.language === 'ja' ? 'ja-JP' : 'en-US');
    }
};

export default function NotificationsModal({
    isOpen,
    onClose,
}: NotificationsModalProps) {
    const { t, i18n } = useTranslation('common');
    const [notifications, setNotifications] = useState<ApiNotification[]>([]);
    const [unreadCount, setUnreadCount] = useState(0);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [translatedNotifications, setTranslatedNotifications] = useState<Map<number, NotificationTranslation>>(new Map());
    const [isTranslating, setIsTranslating] = useState(false);
    const [isTranslationEnabled, setIsTranslationEnabled] = useState(false);

    // Check if we need to translate (English mode AND translation enabled)
    const needsTranslation = i18n.language === 'en' && isTranslationEnabled;

    // Check translation settings on mount
    useEffect(() => {
        const checkTranslationSettings = async () => {
            try {
                const isConfigured = await translationClient.isConfiguredPublic();
                setIsTranslationEnabled(isConfigured);
            } catch {
                setIsTranslationEnabled(false);
            }
        };
        checkTranslationSettings();
    }, []);

    useEffect(() => {
        if (isOpen) {
            // 分析イベント: モーダルオープンを追跡
            AnalyticsLogger.getInstance().trackModalOpen('notifications');
            loadNotifications();
        }
    }, [isOpen]);

    // Translate notifications when loaded and in English mode
    useEffect(() => {
        const translateNotifications = async () => {
            if (!needsTranslation || notifications.length === 0) {
                setTranslatedNotifications(new Map());
                return;
            }

            setIsTranslating(true);
            try {
                const notificationsToTranslate = notifications.map(n => ({
                    id: n.id,
                    title: n.title,
                    message: n.message || undefined,
                }));

                const result = await translationClient.translateNotifications(
                    notificationsToTranslate,
                    'EN'
                );

                console.log('[NotificationsModal] Translated notifications:',
                    `cached=${result.cached_count}, translated=${result.translated_count}`);

                const translationMap = new Map<number, NotificationTranslation>();
                result.translations.forEach(t => {
                    translationMap.set(t.id, t);
                });
                setTranslatedNotifications(translationMap);
            } catch (err) {
                console.error('[NotificationsModal] Translation error:', err);
                setTranslatedNotifications(new Map());
            } finally {
                setIsTranslating(false);
            }
        };

        translateNotifications();
    }, [notifications, needsTranslation]);

    // Helper to get translated title
    const getTranslatedTitle = (notification: ApiNotification): string => {
        if (needsTranslation && translatedNotifications.has(notification.id)) {
            return translatedNotifications.get(notification.id)!.title;
        }
        return notification.title;
    };

    // Helper to get translated message
    const getTranslatedMessage = (notification: ApiNotification): string | null => {
        if (!notification.message) return null;
        if (needsTranslation && translatedNotifications.has(notification.id)) {
            const translated = translatedNotifications.get(notification.id)!;
            return translated.message || notification.message;
        }
        return notification.message;
    };

    const handleClose = () => {
        // 分析イベント: モーダルクローズを追跡
        AnalyticsLogger.getInstance().trackModalClose('notifications');
        onClose();
    };

    const loadNotifications = async () => {
        setIsLoading(true);
        setError(null);
        try {
            const response = await apiClient.getNotifications();
            setNotifications(response.notifications);
            setUnreadCount(response.unread_count);

            // 分析イベント: 通知一覧表示を追跡
            AnalyticsLogger.getInstance().trackNotificationListView(response.unread_count);

            // Mark all as read after fetching
            const unreadIds = response.notifications
                .filter((n) => !n.is_read)
                .map((n) => n.id);
            if (unreadIds.length > 0) {
                await apiClient.markNotificationsAsRead(unreadIds);
            }
        } catch (err) {
            setError(err instanceof Error ? err.message : t('notifications.fetchError'));
        } finally {
            setIsLoading(false);
        }
    };

    const handleNotificationClick = (notification: ApiNotification) => {
        // 分析イベント: 通知クリックを追跡
        AnalyticsLogger.getInstance().trackNotificationClick(
            notification.id.toString(),
            notification.sender_type
        );
        if (notification.link_url) {
            window.open(notification.link_url, '_blank', 'noopener,noreferrer');
        }
    };

    if (!isOpen) return null;

    return (
        <div
            className="fixed inset-0 bg-black/50 flex items-center justify-center z-50"
            onClick={(e) => e.target === e.currentTarget && handleClose()}
        >
            <div className="bg-gray-100 w-full max-w-4xl max-h-[85vh] overflow-hidden flex flex-col mx-4 rounded-lg">
                {/* Header */}
                <div className="bg-gradient-to-r from-pink-100 to-pink-200 px-6 py-4 flex items-center justify-between">
                    <div className="flex items-center gap-4">
                        <button
                            onClick={handleClose}
                            className="bg-white text-pink-700 px-4 py-2 border border-pink-300 font-semibold hover:bg-pink-50 transition-colors rounded text-sm flex items-center gap-2"
                        >
                            {t('notifications.back')}
                        </button>
                        <h2 className="text-xl font-bold text-pink-900 flex items-center gap-2">
                            {t('notifications.title')}
                            {unreadCount > 0 && (
                                <span className="bg-red-500 text-white text-xs font-bold rounded-full px-2 py-0.5">
                                    {unreadCount}
                                </span>
                            )}
                        </h2>
                    </div>
                    <button
                        onClick={handleClose}
                        className="text-pink-700 hover:text-pink-900 text-2xl leading-none font-bold"
                    >
                        ×
                    </button>
                </div>

                {/* Content */}
                <div className="flex-1 overflow-y-auto p-4">
                    {isLoading ? (
                        <div className="text-center py-12 text-gray-500">
                            <p>{t('notifications.loading')}</p>
                        </div>
                    ) : error ? (
                        <div className="text-center py-12 text-red-500">
                            <p>{error}</p>
                        </div>
                    ) : notifications.length === 0 ? (
                        <div className="text-center py-16 text-gray-500">
                            <div className="text-5xl mb-4 opacity-50">🔔</div>
                            <p className="text-sm">{t('notifications.noNotifications')}</p>
                        </div>
                    ) : (
                        <div className="bg-white rounded-lg overflow-hidden shadow-sm">
                            {notifications.map((notification, index) => (
                                <div
                                    key={notification.id}
                                    className={`flex items-center gap-4 p-4 cursor-pointer transition-colors hover:bg-gray-50 relative ${
                                        index !== notifications.length - 1 ? 'border-b border-gray-100' : ''
                                    }`}
                                    onClick={() => handleNotificationClick(notification)}
                                >
                                    {/* Unread indicator */}
                                    {!notification.is_read && (
                                        <div className="absolute left-2 top-1/2 -translate-y-1/2 w-2 h-2 bg-red-500 rounded-full" />
                                    )}

                                    {/* Image */}
                                    <div className="w-[150px] h-[100px] bg-gray-100 rounded flex-shrink-0 overflow-hidden ml-2">
                                        {notification.image_url ? (
                                            <NotificationImage
                                                key={resolveMediaUrl(notification.image_url)}
                                                src={resolveMediaUrl(notification.image_url)}
                                                alt={t('notifications.imageAlt')}
                                            />
                                        ) : (
                                            <div className="w-full h-full flex items-center justify-center text-gray-400 text-3xl">
                                                📢
                                            </div>
                                        )}
                                    </div>

                                    {/* Content */}
                                    <div className="flex-1 min-w-0 text-left">
                                        {/* Category badge */}
                                        <span className="inline-block bg-gray-200 text-gray-700 px-3 py-1 rounded text-xs font-semibold mb-2">
                                            {getSenderTypeLabel(notification.sender_type, t)}
                                        </span>

                                        {/* Title */}
                                        <div className="text-base font-semibold text-gray-900 mb-1 leading-relaxed line-clamp-2 text-left">
                                            {isTranslating && needsTranslation ? (
                                                <span className="text-gray-400">{t('notifications.translating')}</span>
                                            ) : (
                                                getTranslatedTitle(notification)
                                            )}
                                        </div>

                                        {/* Message */}
                                        {getTranslatedMessage(notification) && (
                                            <div className="text-sm text-gray-600 mb-2 leading-relaxed line-clamp-3 text-left">
                                                {isTranslating && needsTranslation ? (
                                                    <span className="text-gray-400">...</span>
                                                ) : (
                                                    getTranslatedMessage(notification)
                                                )}
                                            </div>
                                        )}

                                        {/* Meta info */}
                                        <div className="flex items-center justify-start gap-4 text-xs text-gray-500">
                                            <span>{formatTime(notification.created_at, t, i18n)}</span>
                                            {notification.sender_name && (
                                                <span>{notification.sender_name}</span>
                                            )}
                                        </div>
                                    </div>

                                    {/* Arrow */}
                                    <div className="text-gray-300 text-2xl flex-shrink-0 hover:text-gray-400">
                                        ›
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
}
